import { useCallback, useEffect, useRef, useState } from 'react';
import TileLayer from 'ol/layer/Tile';
import type { XYZ } from 'ol/source';
import type TileSource from 'ol/source/Tile';
import type { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';

interface TileLoadingState {
  /** Whether tiles are currently loading */
  isLoading: boolean;
  /** Number of tiles that have finished loading (success + error) */
  loaded: number;
  /** Total number of tiles requested since last reset */
  total: number;
  /** Number of tiles that failed to load */
  errored: number;
  /** Progress as 0–1 fraction (0 when nothing requested) */
  progress: number;
}

// If no tile progress is made within this time, consider loading done.
// Handles tiles that silently fail without triggering tileloaderror.
const STALE_TIMEOUT_MS = 5000;

// After all known tiles complete, wait this long before declaring "done".
// OL queues tile requests in batches — this prevents false "done" transitions
// between batches.
const SETTLE_DELAY_MS = 500;

/**
 * Tracks tile loading progress for an OpenLayers TileLayer<XYZ>.
 *
 * Listens to `tileloadstart`, `tileloadend`, and `tileloaderror` events
 * on the source. Resets counters whenever the source URL changes (i.e.
 * when `source.setUrl()` is called, which triggers a `change` event).
 *
 * Includes:
 * - A settle delay: only reports isLoading=false after 500ms of no new tile
 *   activity (prevents false "done" between OL request batches).
 * - A stale timeout: if no tiles complete within 5s, force-completes.
 *
 * Returns live loading state that can drive spinners, progress toasts, etc.
 */
export function useABBTileLoadingProgress(
  layer: TileLayer<TileSource> | null | undefined,
): TileLoadingState {
  const [state, setState] = useState<TileLoadingState>({
    isLoading: false,
    loaded: 0,
    total: 0,
    errored: 0,
    progress: 0,
  });

  const countersRef = useRef({ loaded: 0, total: 0, errored: 0 });
  const eventKeysRef = useRef<EventsKey[]>([]);
  const staleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (staleTimerRef.current) {
      clearTimeout(staleTimerRef.current);
      staleTimerRef.current = null;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const resetCounters = useCallback(() => {
    countersRef.current = { loaded: 0, total: 0, errored: 0 };
    clearTimers();
    setState({
      isLoading: false,
      loaded: 0,
      total: 0,
      errored: 0,
      progress: 0,
    });
  }, [clearTimers]);

  useEffect(() => {
    if (!layer) {
      resetCounters();
      return;
    }

    const source = layer.getSource() as XYZ | null;
    if (!source) {
      resetCounters();
      return;
    }

    const markComplete = () => {
      const { loaded, total, errored } = countersRef.current;
      setState({
        isLoading: false,
        loaded,
        total,
        errored,
        progress: total > 0 ? loaded / total : 0,
      });
    };

    const forceComplete = () => {
      const { loaded, total } = countersRef.current;
      const remaining = total - loaded;
      countersRef.current.loaded = total;
      countersRef.current.errored += remaining;
      markComplete();
    };

    const scheduleSettle = () => {
      // Cancel any pending settle
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        const { loaded, total } = countersRef.current;
        if (loaded >= total && total > 0) {
          markComplete();
        }
      }, SETTLE_DELAY_MS);
    };

    const resetStaleTimer = () => {
      if (staleTimerRef.current) {
        clearTimeout(staleTimerRef.current);
      }
      staleTimerRef.current = setTimeout(forceComplete, STALE_TIMEOUT_MS);
    };

    const updateState = () => {
      const { loaded, total, errored } = countersRef.current;
      const pending = loaded < total;

      if (pending) {
        // Still loading — report progress, keep isLoading true
        if (settleTimerRef.current) {
          clearTimeout(settleTimerRef.current);
          settleTimerRef.current = null;
        }
        setState({
          isLoading: true,
          loaded,
          total,
          errored,
          progress: total > 0 ? loaded / total : 0,
        });
        resetStaleTimer();
      } else if (total > 0) {
        // All known tiles done — wait for settle before declaring complete
        if (staleTimerRef.current) {
          clearTimeout(staleTimerRef.current);
          staleTimerRef.current = null;
        }
        // Update progress numbers but keep isLoading true during settle
        setState({
          isLoading: true,
          loaded,
          total,
          errored,
          progress: 1,
        });
        scheduleSettle();
      }
    };

    const onTileLoadStart = () => {
      // If previous batch completed (loaded >= total), reset for new batch.
      // This handles zoom/pan triggering new tile loads without a URL change.
      const { loaded, total } = countersRef.current;
      if (total > 0 && loaded >= total) {
        countersRef.current = { loaded: 0, total: 0, errored: 0 };
        clearTimers();
      }
      countersRef.current.total += 1;
      updateState();
    };

    const onTileLoadEnd = () => {
      countersRef.current.loaded += 1;
      updateState();
    };

    const onTileLoadError = () => {
      countersRef.current.loaded += 1;
      countersRef.current.errored += 1;
      updateState();
    };

    // Reset counters when source URL changes (new timepoint selected).
    // Set isLoading: true immediately — tiles will start loading shortly.
    const onSourceChange = () => {
      countersRef.current = { loaded: 0, total: 0, errored: 0 };
      clearTimers();
      setState({
        isLoading: true,
        loaded: 0,
        total: 0,
        errored: 0,
        progress: 0,
      });
    };

    const keys: EventsKey[] = [
      source.on('tileloadstart', onTileLoadStart),
      source.on('tileloadend', onTileLoadEnd),
      source.on('tileloaderror', onTileLoadError),
      source.on('change', onSourceChange),
    ];

    eventKeysRef.current = keys;

    return () => {
      unByKey(keys);
      eventKeysRef.current = [];
      clearTimers();
    };
  }, [layer, resetCounters, clearTimers]);

  return state;
}
